import { useCallback, useEffect, useState } from 'react';
import { Trans } from '../i18n/Trans';
import { ApiHelper } from '../helper/ApiHelper';
import { Modal } from '../components/Modal';
import { QuoteTable, type QuoteResult } from '../components/QuoteTable';

interface OrderBrief {
  id: number;
  name: string;
  phone: string;
  email: string;
  company: string;
  address: string;
  /** 非空即"这单要开票"（'special' 专票 / 'plain' 普票），空串就是不开票 */
  invoiceType: string;
  taxNo: string;
  userName: string;
  userEmail: string;
  fileName: string;
  lineCount: number;
  pricedCount: number;
  currency: string;
  total: number;
  status: string;
  remark: string;
  createdAt: string;
}

const STATUSES = ['new', 'processing', 'quoted', 'done', 'canceled'];

/** 详情弹窗开关放 URL 的哪个参数上 */
const Q = 'order';

/** 当前地址里要打开的订单号（没有就 0） */
function orderInUrl(): number {
  const v = Number(new URLSearchParams(window.location.search).get(Q));
  return Number.isInteger(v) && v > 0 ? v : 0;
}

/** 只改 order 这一个参数，别的查询参数原样留着 */
function urlWith(id: number): string {
  const u = new URL(window.location.href);
  if (id) u.searchParams.set(Q, String(id));
  else u.searchParams.delete(Q);
  return u.pathname + (u.search || '') + u.hash;
}

/**
 * 订单（后台）。**管理员看全部单，普通用户只看自己的** —— 由后端按 order.manage 决定，
 * 前端只是照着 seeAll 换个说明文案，不自己判权限。
 */
export function OrdersPage({ canSeeCost }: { canSeeCost: boolean }) {
  const [list, setList] = useState<OrderBrief[] | null>(null);
  const [seeAll, setSeeAll] = useState(false);
  const [open, setOpen] = useState<{ row: OrderBrief; quote: QuoteResult } | null>(null);
  const [err, setErr] = useState('');
  const [dling, setDling] = useState(false);

  const load = () => {
    ApiHelper.call<{ list: OrderBrief[]; seeAll: boolean }>('/api/orders')
      .then((r) => { setList(r.list); setSeeAll(r.seeAll); })
      .catch((e) => { setList([]); setErr(e instanceof Error ? e.message : String(e)); });
  };
  useEffect(load, []);

  // 打开详情拆成两个入口：restore 只显示不动历史，view = restore + 压一条历史。
  // 从 URL 还原、按返回键回到某条详情都只能走 restore——走 view 会每次多压一条，
  // 用户得连按好几次返回才退得出这个页面。
  const restore = useCallback(async (id: number) => {
    setErr('');
    try {
      const r = await ApiHelper.call<{ item: OrderBrief & { quote: QuoteResult } }>(`/api/orders/${id}`);
      setOpen({ row: r.item, quote: r.item.quote });
    } catch (e) {
      setOpen(null);
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const view = (id: number) => {
    window.history.pushState({}, '', urlWith(id));
    void restore(id);
  };

  // 关闭用 replaceState：不再往历史里塞一条"关掉了"，否则返回键又把弹窗打开
  const close = () => {
    setOpen(null);
    window.history.replaceState({}, '', urlWith(0));
  };

  // 带 ?order=N 进来（别人发的链接、刷新、手机上返回）直接还原那条详情
  useEffect(() => {
    const id = orderInUrl();
    if (id) void restore(id);
    const onPop = () => {
      const now = orderInUrl();
      if (now) void restore(now);
      else setOpen(null);   // 返回键关弹窗：只卸载界面，绝不再动历史
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [restore]);

  // 下载这张单当时那份报价（后端按权限再脱敏一次，成本列不会漏给没权限的人）
  const download = async (row: OrderBrief, quote: QuoteResult) => {
    setErr('');
    setDling(true);
    try {
      await ApiHelper.download('/api/quote/export', { quote }, `hip_order_${row.id}.xlsx`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDling(false);
    }
  };

  const setStatus = async (id: number, status: string) => {
    setErr('');
    try {
      await ApiHelper.call(`/api/orders/${id}/status`, 'PUT', { status });
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div>
      <h1 className="page-title">{Trans.t('tab.orders')}</h1>
      <p className="page-sub">{Trans.t(seeAll ? 'order.subAdmin' : 'order.subMine')}</p>

      {err && <div className="error-box">{err}</div>}

      {list !== null && list.length === 0 && <p className="hint">{Trans.t('order.empty')}</p>}

      {list !== null && list.length > 0 && (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                {/* 「查看」放最左边：这是每行最常点的动作，放在行首手指/鼠标最好找，
                    也不会因为表格横向滚动而跑出可视区（右端那一列在窄屏上要拖到底才看得见） */}
                <th />
                <th className="num">#</th>
                <th>{Trans.t('order.col.time')}</th>
                {seeAll && <th>{Trans.t('order.col.who')}</th>}
                <th>{Trans.t('order.col.contact')}</th>
                <th>{Trans.t('order.col.address')}</th>
                <th>{Trans.t('order.col.invoice')}</th>
                <th>{Trans.t('order.col.file')}</th>
                <th className="num">{Trans.t('order.col.lines')}</th>
                <th className="num">{Trans.t('order.col.total')}</th>
                <th>{Trans.t('order.col.status')}</th>
                <th>{Trans.t('order.col.remark')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((o) => (
                <tr key={o.id}>
                  <td>
                    <button className="btn ghost row-view" onClick={() => view(o.id)}>
                      {Trans.t('history.view')}
                    </button>
                  </td>
                  <td className="num">{o.id}</td>
                  <td className="nowrap">{o.createdAt.replace('T', ' ').slice(0, 19)}</td>
                  {seeAll && <td>{o.userName || o.userEmail}<div className="sub">{o.userEmail}</div></td>}
                  <td>
                    {o.name || '-'}
                    <div className="sub">{o.phone}{o.company ? ` · ${o.company}` : ''}</div>
                  </td>
                  <td className="note">{o.address || '-'}</td>
                  <td>
                    {o.invoiceType
                      ? <>
                        <span className="pill">{Trans.t(`order.invoice.${o.invoiceType}`)}</span>
                        <div className="sub">{o.taxNo}</div>
                      </>
                      : <span className="note">{Trans.t('order.invoice.none')}</span>}
                  </td>
                  <td>{o.fileName}</td>
                  <td className="num">{o.pricedCount}/{o.lineCount}</td>
                  <td className="num strong">{o.total.toFixed(2)} {o.currency}</td>
                  <td>
                    {seeAll ? (
                      <select
                        className="status-pick"
                        value={o.status}
                        onChange={(e) => setStatus(o.id, e.target.value)}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{Trans.t(`order.status.${s}`)}</option>
                        ))}
                      </select>
                    ) : <span className={`pill st-${o.status}`}>{Trans.t(`order.status.${o.status}`)}</span>}
                  </td>
                  <td className="note">{o.remark}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 详情走弹窗，不再接在列表下面：列表一长，点完那份报价就被冲到页面底部，
          用户根本不知道该往哪看。弹窗是"点了就在眼前"，关掉还回到列表原位。 */}
      {open && (
        <Modal title={`${Trans.t('order.detailOf')} #${open.row.id}`} onClose={close} wide>
          <div className="order-sum">
            <span>{Trans.t('order.col.contact')} <b>{open.row.name || '-'}</b></span>
            <span>{Trans.t('order.f.company')} <b>{open.row.company || '-'}</b></span>
            <span>{Trans.t('order.col.invoice')} <b>
              {open.row.invoiceType
                ? Trans.t(`order.invoice.${open.row.invoiceType}`)
                : Trans.t('order.invoice.none')}
              {open.row.taxNo ? ` · ${open.row.taxNo}` : ''}
            </b></span>
            <span>{Trans.t('order.col.total')} <b>
              {open.row.total.toFixed(2)} {open.row.currency}
            </b></span>
          </div>

          <QuoteTable quote={open.quote} canSeeCost={canSeeCost} />

          <div className="field">
            <button
              className="btn accent"
              disabled={dling}
              onClick={() => download(open.row, open.quote)}
            >
              {dling ? Trans.t('order.downloading') : Trans.t('order.download')}
            </button>
            <span className="hint">{Trans.t('order.downloadHint')}</span>
          </div>
        </Modal>
      )}
    </div>
  );
}
